import { Button, Container, Grid, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { BasicMerchant } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  SelectData,
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import {
  BasicBranch,
  BasicPartner,
  BasicReseller,
} from '../../type/select.types';

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={6}>
      {children}
    </Grid.Col>
  );
};

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  setIsLoadPartners: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadPartners: boolean;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  setSubmitApplied: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: UserManagementFilterMode;
  isLoading: boolean;
}

function UserManagementFilter(props: Props) {
  const {
    filterValues,
    setIsLoadPartners,
    isLoadPartners,
    filterSelectData,
    setSubmitApplied,
    mode,
    isLoading,
  } = props;
  const axiosPrivate = useAxiosPrivate();

  const selectLoadingData = useForm({
    initialValues: {
      isPartnersLoading: false,
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
    },
  });

  const form = useForm({
    initialValues: filterValues.values,
  });

  const findValueInData = (data: SelectData[], value: string): boolean => {
    for (let i = 0; i < data?.length; i++) {
      if (data[i].value === value) {
        return true;
      }
    }
    return false;
  };

  const allFieldsFilled =
    !form.values.partner ||
    !form.values.reseller ||
    !form.values.merchant ||
    !form.values.merchantBranch;

  const disableFields = !form.values.partner;

  const noData = 'No data';
  const loadingData = 'Loading...';

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const response = await axiosPrivate.get('/web/select/partners');
        const partners = response.data?.items?.map((partner: BasicPartner) => {
          return {
            label: partner.partnerName,
            value: partner.partnerId.toString(),
          };
        });
        filterSelectData.setFieldValue('partnersData', partners);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-partners-data',
          message:
            error.error ||
            err.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      }
    };

    //so that partners isn't loaded every time component
    //remounts when it comes back from transaction details
    if (isLoadPartners) {
      loadPartners();
    } else {
      setIsLoadPartners(true);
    }
  }, []);

  useDidMountEffect(() => {
    let ignore = false;
    const loadResellers = async () => {
      selectLoadingData.setFieldValue('isResellersLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/resellers', {
          params: {
            partnerId: form.values.partner,
          },
        });
        const resellers: SelectData[] = response.data?.items?.map(
          (reseller: BasicReseller) => {
            return {
              label: reseller.resellerName,
              value: reseller.resellerId.toString(),
            };
          }
        );

        //@ref https://stackoverflow.com/a/69725148
        if (!ignore) {
          const found = findValueInData(resellers, form.values.reseller);

          if (!found) {
            form.setFieldValue('reseller', '');
          }
          filterSelectData.setFieldValue('resellersData', resellers);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-resellers-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isResellersLoading', false);
      }
    };
    if (!disableFields && form.values.partner !== noData) {
      loadResellers();
    } else {
      form.setFieldValue('reseller', '');
    }

    return () => {
      ignore = true;
    };
  }, [form.values.partner]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchants = async () => {
      selectLoadingData.setFieldValue('isMerchantsLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/merchants', {
          params: {
            partnerId: form.values.partner,
            resellerId: form.values.reseller,
          },
        });

        const merchants = response.data?.items?.map(
          (merchant: BasicMerchant) => {
            return {
              label: merchant.merchantName,
              value: merchant.merchantId.toString(),
            };
          }
        );

        if (!ignore) {
          const found = findValueInData(merchants, form.values.merchant);

          if (!found) {
            form.setFieldValue('merchant', '');
          }
          filterSelectData.setFieldValue('merchantsData', merchants);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-merchants-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isMerchantsLoading', false);
      }
    };

    if (!disableFields && form.values.reseller) {
      loadMerchants();
    } else {
      form.setFieldValue('merchant', '');
      filterSelectData.setFieldValue('merchantsData', []);
    }

    return () => {
      ignore = true;
    };
  }, [form.values.reseller]);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchantBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches', {
          params: {
            partnerId: form.values.partner,
            resellerId: form.values.reseller,
            merchantId: form.values.merchant,
          },
        });

        const merchantBranches = response.data?.items?.map(
          (merchant: BasicBranch) => {
            return {
              label: merchant.branchName,
              value: merchant.branchId.toString(),
            };
          }
        );

        if (!ignore) {
          const found = findValueInData(
            merchantBranches,
            form.values.merchantBranch
          );

          if (!found) {
            form.setFieldValue('merchantBranch', '');
          }
          filterSelectData.setFieldValue(
            'merchantBranchesData',
            merchantBranches
          );
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    if (!disableFields && form.values.merchant) {
      loadMerchantBranches();
    } else {
      form.setFieldValue('merchantBranch', '');
      filterSelectData.setFieldValue('merchantBranchesData', []);
    }

    return () => {
      ignore = true;
    };
  }, [form.values.merchant]);

  const handleSubmit = () => {
    filterValues.setValues(form.values);
    setSubmitApplied((prev) => {
      return !prev;
    });
  };

  return (
    <>
      <Container fluid p={0} m={0}>
        <Grid>
          <ResponsiveCol>
            <Select
              label='Select Partner'
              data={filterSelectData.values.partnersData}
              onChange={(value) => {
                const partner = value || '';
                form.setFieldValue('partner', partner);
              }}
              value={form.values.partner}
              clearable
              searchable
            />
          </ResponsiveCol>
          <ResponsiveCol>
            <Select
              label='Select Reseller'
              data={
                selectLoadingData.values.isResellersLoading
                  ? [loadingData]
                  : filterSelectData.values.resellersData?.length === 0 &&
                    !disableFields
                  ? [noData]
                  : filterSelectData.values.resellersData
              }
              value={
                selectLoadingData.values.isResellersLoading
                  ? loadingData
                  : filterSelectData.values.resellersData?.length === 0 &&
                    !disableFields
                  ? noData
                  : form.values.reseller
              }
              disabled={
                selectLoadingData.values.isResellersLoading ||
                disableFields ||
                filterSelectData.values.resellersData?.length === 0 ||
                form.values.partner?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('reseller', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isResellersLoading ||
                  disableFields ||
                  filterSelectData.values.resellersData?.length === 0 ||
                  form.values.partner?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
          <ResponsiveCol>
            <Select
              label='Select Merchant'
              data={
                selectLoadingData.values.isMerchantsLoading
                  ? [loadingData]
                  : filterSelectData.values.merchantsData?.length === 0 &&
                    form.values.reseller
                  ? [noData]
                  : filterSelectData.values.merchantsData
              }
              value={
                selectLoadingData.values.isMerchantsLoading
                  ? loadingData
                  : filterSelectData.values.merchantsData?.length === 0 &&
                    form.values.reseller
                  ? noData
                  : form.values.merchant
              }
              disabled={
                selectLoadingData.values.isMerchantsLoading ||
                filterSelectData.values.merchantsData?.length === 0 ||
                form.values.reseller?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('merchant', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isMerchantsLoading ||
                  filterSelectData.values.merchantsData?.length === 0 ||
                  form.values.reseller?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
          {mode !== UserManagementFilterMode.APPROVE_ENTITY_MODE && (
            <ResponsiveCol>
              <Select
                label='Select Merchant Branch'
                data={
                  selectLoadingData.values.isBranchesLoading
                    ? [loadingData]
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && form.values.merchant
                    ? [noData]
                    : filterSelectData.values.merchantBranchesData
                }
                value={
                  selectLoadingData.values.isBranchesLoading
                    ? loadingData
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && form.values.merchant
                    ? noData
                    : form.values.merchantBranch
                }
                disabled={
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0 ||
                  form.values.merchant?.length === 0
                }
                onChange={(value: string) =>
                  form.setFieldValue('merchantBranch', value)
                }
                clearable={
                  !(
                    selectLoadingData.values.isBranchesLoading ||
                    filterSelectData.values.merchantBranchesData?.length ===
                      0 ||
                    form.values.merchant?.length === 0
                  )
                }
                searchable
              />
            </ResponsiveCol>
          )}
        </Grid>
        <Button
          mt='lg'
          type='submit'
          disabled={
            mode === UserManagementFilterMode.PAYMENT_LINK_MODE
              ? allFieldsFilled
              : mode === UserManagementFilterMode.APPROVE_ENTITY_MODE
              ? false
              : isLoading
              ? isLoading
              : disableFields
          }
          onClick={handleSubmit}
        >
          Apply
        </Button>
      </Container>
    </>
  );
}

export default UserManagementFilter;
